import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  TrendingUp,
  DollarSign,
  Building2,
  Calendar,
  Loader2,
  CheckCircle2,
  Clock,
  Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentSales() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salesData, setSalesData] = useState(null);
  const [buildings, setBuildings] = useState([]);

  useEffect(() => {
    fetchSalesData();
  }, [selectedMonth]);

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/managing-agents/sales?month=${selectedMonth}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSalesData(data);
        setBuildings(data.buildings || []);
      } else {
        // Demo data
        setDemoData();
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    const demoBuildings = [
      { 
        hoa_id: 'demo_1', 
        name: 'Palm Gardens Complex', 
        total_units: 24, 
        subscription_date: '2026-01-15',
        subscription_amount: 840, // 24 * 35
        commission_rate: 10,
        commission_amount: 84,
        status: 'paid'
      },
      { 
        hoa_id: 'demo_2', 
        name: 'Ocean View Apartments', 
        total_units: 48, 
        subscription_date: '2026-02-01',
        subscription_amount: 1680, // 48 * 35
        commission_rate: 10,
        commission_amount: 168,
        status: 'paid'
      },
      { 
        hoa_id: 'demo_3', 
        name: 'Riverside Villas', 
        total_units: 18, 
        subscription_date: '2026-03-10',
        subscription_amount: 630, // 18 * 35
        commission_rate: 10,
        commission_amount: 63,
        status: 'pending'
      },
    ];

    setSalesData({
      total_buildings: 3,
      total_units: 90,
      total_subscriptions: 3150,
      total_commission: 315,
      paid_commission: 252,
      pending_commission: 63,
    });
    setBuildings(demoBuildings);
  };

  const getMonthOptions = () => {
    const months = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        value: date.toISOString().slice(0, 7),
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return months;
  };

  const formatCurrency = (amount) => `R ${amount.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/agent')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
                Sales & Commissions
              </h1>
              <p className="text-slate-500 mt-1">Track your building signups and commission earnings</p>
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
                <CardContent className="p-4">
                  <p className="text-indigo-100 text-sm">Buildings Added</p>
                  <p className="text-3xl font-bold">{salesData?.total_buildings || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-sky-500 to-sky-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sky-100 text-sm">Total Units</p>
                  <p className="text-3xl font-bold">{salesData?.total_units || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-4">
                  <p className="text-emerald-100 text-sm">Total Commissions</p>
                  <p className="text-3xl font-bold">{formatCurrency(salesData?.total_commission || 0)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="p-4">
                  <p className="text-amber-100 text-sm">Pending Payout</p>
                  <p className="text-3xl font-bold">{formatCurrency(salesData?.pending_commission || 0)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Commission Breakdown */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Commission Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Commission Rate</p>
                    <p className="text-2xl font-bold text-indigo-600">10%</p>
                    <p className="text-xs text-slate-400">of subscription value</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-sm text-slate-500">Paid Out</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(salesData?.paid_commission || 0)}</p>
                    <p className="text-xs text-slate-400">deposited to your account</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(salesData?.pending_commission || 0)}</p>
                    <p className="text-xs text-slate-400">awaiting building payment</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Buildings Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Building Sales</CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Building</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Units</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Subscription</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Commission (10%)</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildings.map((building) => (
                        <tr key={building.hoa_id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{building.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{building.total_units}</td>
                          <td className="py-3 px-4 text-slate-600">{formatCurrency(building.subscription_amount)}</td>
                          <td className="py-3 px-4 font-medium text-emerald-600">{formatCurrency(building.commission_amount)}</td>
                          <td className="py-3 px-4">
                            <Badge className={building.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {building.status === 'paid' ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" /> Paid</>
                              ) : (
                                <><Clock className="w-3 h-3 mr-1" /> Pending</>
                              )}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {new Date(building.subscription_date).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {buildings.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-slate-500">
                            No sales recorded for this month
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {buildings.length > 0 && (
                      <tfoot>
                        <tr className="bg-slate-50 font-medium">
                          <td className="py-3 px-4">Total</td>
                          <td className="py-3 px-4">{salesData?.total_units || 0}</td>
                          <td className="py-3 px-4">{formatCurrency(salesData?.total_subscriptions || 0)}</td>
                          <td className="py-3 px-4 text-emerald-600">{formatCurrency(salesData?.total_commission || 0)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
